import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'

const fieldBase = css`
    width: 100%;
    display: block;
    background-color: rgba(255, 255, 255, 0.06);
    border: 2px solid #ffffff;
    border-radius: var(--radius-field);
    color: var(--ink-inverse);
    font-family: var(--font-body);
    font-size: 16px;
    line-height: 1.4;
    padding: 14px 16px;
    outline: none;
    transition: border-color var(--duration-ui) var(--ease-out),
        box-shadow var(--duration-ui) var(--ease-out);

    &::placeholder {
        color: rgba(255, 255, 255, 0.45);
    }

    &:hover:not(:focus):not(:disabled) {
        border-color: #ffffff;
    }

    &:focus {
        border-color: var(--brand);
        box-shadow: 0 0 10px rgba(112, 198, 24, 0.25);
    }

    &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }
`

export const ContactPage = styled.main`
    background-color: var(--surface-0);
    color: var(--ink-inverse);
`

export const ContactHero = styled.section`
    position: relative;
    background-color: var(--brand);
    padding: 48px 0 0;

    @media (max-width: 999px) {
        padding: 40px 0 0;
    }

    @media (max-width: 690px) {
        padding: 32px 0 0;
    }
`

export const ContactFormBand = styled.div`
    background-color: #ffffff;
    padding: 28px 0 48px;

    @media (max-width: 999px) {
        padding: 24px 0 40px;
    }

    @media (max-width: 690px) {
        padding: 20px 0 32px;
    }
`

export const ContactInner = styled.div`
    position: relative;
    z-index: 1;
    width: 66.666%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1%;

    @media (max-width: 999px) {
        width: 100%;
        max-width: none;
        padding: 0 24px;
    }

    @media (max-width: 690px) {
        padding: 0 20px;
    }
`

export const ContactIntro = styled.div`
    padding-bottom: 36px;

    @media (max-width: 999px) {
        padding-bottom: 28px;
    }

    @media (max-width: 690px) {
        padding-bottom: 24px;
    }

    h1 {
        margin: 0 0 16px;
        color: var(--ink);
        font-family: var(--font-display);
        font-size: 72px;
        font-weight: 400;
        line-height: 1;
        letter-spacing: 1px;
        text-transform: uppercase;
    }

    p {
        margin: 0;
        max-width: 36em;
        color: var(--ink);
        font-family: var(--font-body);
        font-size: var(--text-body);
        line-height: var(--leading-body);
    }

    @media (max-width: 1300px) {
        h1 {
            font-size: 54px;
        }
    }

    @media (max-width: 999px) {
        margin-bottom: 24px;

        h1 {
            font-size: 47px;
        }
    }

    @media (max-width: 690px) {
        h1 {
            font-size: 40px;
        }

        p {
            font-size: 16px;
            line-height: 24px;
        }
    }
`

export const ContactFormCard = styled.div`
    background-color: var(--surface-0);
    color: var(--ink-inverse);
    padding: 4%;
    box-shadow: rgba(0, 0, 0, 0.04) 0 1px 0, rgba(0, 0, 0, 0.05) 0 2px 7px,
        rgba(0, 0, 0, 0.06) 0 12px 22px;

    @media (max-width: 999px) {
        padding: 28px 22px 32px;
    }

    @media (max-width: 690px) {
        padding: 24px 18px 28px;
    }
`

export const RequiredLegend = styled.p`
    margin: 0 0 8px;
    color: rgba(255, 255, 255, 0.72);
    font-family: var(--font-body);
    font-size: 14px;
    line-height: 1.5;

    .req {
        color: #ff5c4d;
        font-weight: 700;
        font-size: 22px;
        line-height: 1;
        vertical-align: -2px;
    }
`

export const ContactForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

export const Field = styled.div`
    margin-top: 16px;
`

export const FieldLabel = styled.label`
    display: block;
    margin: 0 0 8px;
    color: var(--ink-inverse);
    font-family: var(--font-heading);
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;

    .req {
        color: #ff5c4d;
        margin-left: 2px;
        font-size: 1.35em;
        line-height: 1;
    }
`

export const ContactInput = styled.input<{ $invalid?: boolean }>`
    ${fieldBase}
    border-color: ${(props) => (props.$invalid ? '#ff5c4d' : '#ffffff')};
`

export const ContactTextarea = styled.textarea<{ $invalid?: boolean }>`
    ${fieldBase}
    min-height: 220px;
    resize: vertical;
    border-color: ${(props) => (props.$invalid ? '#ff5c4d' : '#ffffff')};

    @media (max-width: 690px) {
        min-height: 180px;
    }
`

export const FieldError = styled.div`
    margin-top: 6px;
    color: #ff5c4d;
    font-family: var(--font-body);
    font-size: 13px;
    line-height: 1.3;
`

export const FormFooter = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 24px;
`

export const SubmitButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    background: var(--brand);
    color: #ffffff;
    border: 0;
    border-radius: var(--radius-pill);
    box-shadow: 0 8px 15px rgba(112, 198, 24, 0.24);
    cursor: pointer;
    font-family: var(--font-heading);
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1;
    padding: 16px 32px;
    transition: background-color var(--duration-ui) var(--ease-out),
        box-shadow var(--duration-ui) var(--ease-out);

    &:hover:not(:disabled) {
        background: var(--brand-hover);
        box-shadow: 0 10px 20px rgba(112, 198, 24, 0.32);
    }

    &:disabled {
        cursor: default;
        opacity: 0.7;
    }
`

export const ConfirmationMessage = styled.div`
    color: var(--ink-inverse);
    font-family: var(--font-body);
    font-size: 18px;
    line-height: 1.6;
    text-align: center;
    padding: 8px 0 12px;
`

export const ConfirmationActions = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;
`

export const HomeLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 160px;
    background: var(--brand);
    color: #ffffff;
    border-radius: var(--radius-pill);
    box-shadow: 0 8px 15px rgba(112, 198, 24, 0.24);
    font-family: var(--font-heading);
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1;
    padding: 16px 32px;
    text-decoration: none;
    transition: background-color var(--duration-ui) var(--ease-out),
        box-shadow var(--duration-ui) var(--ease-out);

    &:hover {
        background: var(--brand-hover);
        color: #ffffff;
        box-shadow: 0 10px 20px rgba(112, 198, 24, 0.32);
    }
`
